import { getMonotoneousRange, Scaler } from "./util";

/**
* A function that maps values on the domain to another value. Values before the start of the domain are clamped 
* to the start value. Values after the end of the domain are clamped to the end value. When the domainRange is 
* positve then the domain start is lower then the domain end. When the domainRange is negative then the domain 
* start is higher then the domain start. The domainValues and rangeValues represent the sequences that were used 
* during the construction of the TransferCharacteristic  
 */
export type TransferCharacteristic = { (num: number): number, domain: [number, number], domainRange: number, domainValues: number[], rangeValues: number[] };

/**
* This functions returns samples of the given domain. The samples are guarenteed to contain the given domain
* start and end values. Therefore the count parameter must be 2 or higher. Optionaly the taken samples are applied
* to the given transfer characteristic. When such function is not given the domain samples get returned.
* @param domain an array  containing the start and the end of the input  range which to sample
* @param count the number of samples to return (must be 2 or higher)
* @param transfer the optional transfer characterisic to apply to the sampled domain values
* @return the array with the possibly transfered samples of the domain range 
 */
export const samples = (domain: [number, number], count: number, transfer?: TransferCharacteristic,): number[] => {
	if (count < 2) {
		throw "Cannot take less then 2 samples from a range.";
	}
	const domainRange = domain[1] - domain[0];
	const rangeSequence = [];
	const transferCharacteristic = transfer ? transfer : (num: number) => num;
	for (let i = 0; i < count; i++) {
		rangeSequence.push(transferCharacteristic(domain[0] + (i / (count - 1)) * domainRange));
	}
	return rangeSequence;
}

/**
* Creates a continuous function that maps input values to their corresponding output.
* Values before the start of the input range are clamped to the start value. Values after
* the end of the input range are clamped to the end value. The values between the given 
* data points in the arrays are calculated using linear interpolation between the two
* neighbouring data points. The number of domain values must equal the number of
* range values. The domain value sequence must be monotoneous (up or down). When
*  only one value is given then the transfer characteristic will equal a horizontal line that
* only ever emits values equal to the sole given range value. In that case the domain value
* can have any value. 
 * @param domain Array of (monotoneous) domain values. I.e. the input values.
 * @param range Array of range values. I.e. the output values
 * @param rangeGain all range values will be multiplied by this gain, defaults to 1.0 (no gain).
 * @return the TransferCharacteristic function that maps the input to the output
 */
export const transferCharacteristic = (domain: number[], range: number[], rangeGain = 1.0): TransferCharacteristic => {
	if (!domain || !range || domain.length != range.length || domain.length === 0) {
		throw "Illegal domain and or range given";
	} else {
		if (domain.length == 1) {
			//profile is a horizontal line
			return Object.assign(
				(_domainValue: number) => range[0] * rangeGain, 
				{
					 domain: <[number, number]>[Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY], 
					 domainRange: Number.POSITIVE_INFINITY,
					 domainValues: domain,
					 rangeValues: range.map(r => r * rangeGain) 
				 } );
		} else {
			//check if the given domain sequence is monotoneously going up or down
			const domainRange = getMonotoneousRange(domain);
			if (domainRange == 0) {
				throw "Illegal domain: not a monotone sequence";
			}
			const lastIndex = domain.length - 1;
			const directionIsUp = domainRange > 0;

			const domainStart = domain[0];
			const domainEnd = domain[lastIndex];
			const domainMin = directionIsUp ? domainStart : domainEnd;
			const domainMax = directionIsUp ? domainEnd : domainStart;

			//create the scalers for each section
			let scalers: ((domainValue: number) => number)[] = [];
			for (let i = 1; i < domain.length; i++) {
				scalers.push(Scaler({ from: domain[i - 1], to: domain[i] }, { from: range[i - 1] * rangeGain, to: range[i] * rangeGain }, false));
			}

			//create the scalaer lookup
			const getScalerIndex = directionIsUp ?
				(domainValue: number): number => {
					let mi = -1;
					while (mi < lastIndex && domainValue >= domain[mi + 1]) mi++;
					return mi;
				} :
				(domainValue: number): number => {
					let mi = -1;
					while (mi < lastIndex && domainValue < domain[mi + 1]) mi++;
					return mi;
				};

			return Object.assign(
				(domainValue: number) => {
					if (domainValue <= domainMin) {
						return directionIsUp ? range[0] * rangeGain : range[lastIndex] * rangeGain;
					} else if (domainValue >= domainMax) {
						return directionIsUp ? range[lastIndex] * rangeGain : range[0] * rangeGain;
					} else {
						//value in range
						const scalerIndex = getScalerIndex(domainValue);
						return scalers[scalerIndex](domainValue);
					}
				},
				{
					domain: <[number, number]>[domainStart, domainEnd],
					domainRange: domainRange , //negative means start is higher then end
					 domainValues: domain,
					 rangeValues: range.map(r => r * rangeGain) 
				});
		}
	}
}
